/**
 * Change Password Card Component
 *
 * A card component that provides password change functionality.
 * Contains form validation and password update logic directly in the component.
 *
 * Features:
 * - Clean card-based UI layout
 * - Title and instruction text
 * - Integrated password change form
 */

import * as React from "react";
import { useContext } from "react";

import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Alert,
  Button,
  Card,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../../context/auth-context";

const ChangePasswordCard: React.FC = () => {
  const [submissionError, setSubmissionError] = React.useState<string>("");
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { updatePassword } = useContext(AuthContext);

  const updatePasswordHandler = (oldPassword: string, newPassword: string) => {
    updatePassword(oldPassword, newPassword, (err) => {
      if (err) {
        console.log(err);
        const errorMessage = "Failed to update password";
        setSubmissionError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      } else {
        setSubmissionError("");
        setSnackbar({
          open: true,
          message: "Password updated successfully",
          severity: "success",
        });
        formik.resetForm();
      }
    });
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formik = useFormik({
    initialValues: {
      originalPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      originalPassword: Yup.string().required("Password is required"),
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values) => {
      const { originalPassword, newPassword } = values;

      try {
        setSubmissionError("");
        updatePasswordHandler(originalPassword, newPassword);
      } catch (error: unknown) {
        console.log(error);

        let errorMessage = "Failed to update password";
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        setSubmissionError(errorMessage);
      }
    },
  });

  const canSubmit = formik.dirty && formik.isValid;

  return (
    <>
      <Card>
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          padding={2}
          spacing={2}
        >
          <Stack spacing={1}>
            <Typography variant="h4">Change Password</Typography>
            <Typography component="h6" variant="h6">
              Please enter the details below to update your password.
            </Typography>
          </Stack>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  id="originalPassword"
                  name="originalPassword"
                  label="Current Password"
                  type="password"
                  variant="outlined"
                  value={formik.values.originalPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.originalPassword &&
                    Boolean(formik.errors.originalPassword)
                  }
                  helperText={
                    formik.touched.originalPassword &&
                    formik.errors.originalPassword
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  id="newPassword"
                  name="newPassword"
                  label="New Password"
                  type="password"
                  variant="outlined"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                  }
                  helperText={
                    formik.touched.newPassword && formik.errors.newPassword
                  }
                  onBlur={formik.handleBlur}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  variant="outlined"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!canSubmit}
                  fullWidth
                >
                  Update Password
                </Button>
              </Grid>

              {submissionError && (
                <Grid size={{ xs: 12 }}>
                  <Alert variant="outlined" severity="error">
                    {submissionError}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </form>
        </Stack>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePasswordCard;
